import {
  ClipboardCheck,
  Mic,
  BookOpen,
  Bookmark,
  Banknote,
  BarChart3,
  Building2,
  CalendarDays,
  ChartCandlestick,
  FileSearch,
  Hourglass,
  Handshake,
  ListOrdered,
  UserRound,
  Clock,
  Coins,
  FileText,
  ArrowLeftRight,
  GitCompare,
  KeyRound,
  Newspaper,
  HeartHandshake,
  HeartPulse,
  ListFilter,
  MessageSquare,
  Quote,
  Radio,
  Receipt,
  Gavel,
  Compass,
  Landmark,
  Scale,
  Search,
  ShieldAlert,
  TreePine,
  TrendingUp,
  Users,
  Vote,
  Zap,
  ExternalLink,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import {
  SidebarLayout,
  type NavEntry,
  type NavGroupConfig,
  type NavItemConfig,
} from '@/components/analytics/SidebarLayout';
import { branding } from '@/config/branding';
import { LanguageSwitcher } from '@/components/layout/language-switcher';
import { ThemeSwitcher } from '@/components/layout/ThemeSwitcher';
import { TimezoneSelector } from '@/components/layout/timezone-selector';
import { useAuth } from '@/hooks/useAuth';

/**
 * Navigation carries only routes that EXIST.
 *
 * The router's catch-all redirects rather than 404s, so a link to an unbuilt
 * route lands on a valid-looking page with no error anywhere -- which is how a
 * whole e2e suite can pass against broken navigation. CADE and ANEEL appear here
 * when their screens are built, not before.
 */
/**
 * Private to the signed-in user, so it is not an agency entitlement. The API-keys entry is
 * for accounts that may hold a key (not a viewer), matching the route's and the server's gate.
 */
function workspaceGroup(canHoldKeys: boolean): NavGroupConfig {
  const children: NavItemConfig[] = [
    { to: '/workspace', icon: Bookmark, labelKey: 'nav.workspace', label: 'My workspace' },
  ];
  if (canHoldKeys) {
    children.push({
      to: '/account/api-keys',
      icon: KeyRound,
      labelKey: 'nav.apiKeys',
      label: 'API keys',
    });
  }
  return {
    key: 'workspace',
    icon: Bookmark,
    labelKey: 'nav.groupWorkspace',
    label: 'My workspace',
    children,
  };
}

export function buildNavigation(
  mayAccess: (agency: string) => boolean,
  isSuperadmin: boolean,
  canHoldKeys = false,
  isReviewer = false,
): NavEntry[] {
  const entries: NavEntry[] = [workspaceGroup(canHoldKeys)];
  if (mayAccess('anatel')) {
    entries.push({
      key: 'anatel',
      icon: Radio,
      labelKey: 'nav.groupAnatel',
      label: 'ANATEL',
      children: [
        {
          to: '/anatel/overview',
          icon: BarChart3,
          labelKey: 'nav.anatelOverview',
          label: 'ANATEL overview',
        },
        {
          to: '/anatel/decisions',
          icon: ListFilter,
          labelKey: 'nav.anatelDecisions',
          label: 'ANATEL decisions',
        },
        {
          to: '/anatel/entities',
          icon: Building2,
          labelKey: 'nav.anatelEntities',
          label: 'ANATEL companies',
        },
        {
          to: '/anatel/sanctions',
          icon: Scale,
          labelKey: 'nav.anatelSanctions',
          label: 'Sanction intelligence',
        },
        {
          to: '/anatel/consistency',
          icon: GitCompare,
          labelKey: 'nav.anatelConsistency',
          label: 'Consistency review',
        },
        {
          to: '/anatel/propositions',
          icon: Quote,
          labelKey: 'nav.anatelPropositions',
          label: 'Reasoning search',
        },
        {
          to: '/anatel/documents',
          icon: FileText,
          labelKey: 'nav.anatelDocuments',
          label: 'Document search',
        },
        {
          to: '/anatel/paf',
          icon: Receipt,
          labelKey: 'nav.anatelPaf',
          label: 'Fiscal track (PAF)',
        },
        {
          to: '/anatel/paf/consistency',
          icon: GitCompare,
          labelKey: 'nav.anatelPafConsistency',
          label: 'Fiscal consistency review',
        },
        {
          to: '/anatel/acordaos',
          icon: Users,
          labelKey: 'nav.anatelAcordaos',
          label: 'Collegiate (Acordaos)',
        },
        {
          to: '/anatel/conselho',
          icon: Users,
          labelKey: 'nav.anatelConselho',
          label: 'Conselho Diretor',
        },
        {
          to: '/anatel/infracoes',
          icon: Banknote,
          labelKey: 'nav.anatelInfracoes',
          label: 'Infractions & payment',
        },
      ],
    });
  }
  if (mayAccess('aneel')) {
    entries.push({
      key: 'aneel',
      icon: Zap,
      labelKey: 'nav.groupAneel',
      label: 'ANEEL',
      children: [
        {
          to: '/aneel/overview',
          icon: BarChart3,
          labelKey: 'nav.aneelOverview',
          label: 'ANEEL enforcement',
        },
        {
          to: '/aneel/cases',
          icon: ListFilter,
          labelKey: 'nav.aneelCases',
          label: 'Infraction notices',
        },
        {
          to: '/aneel/penalties',
          icon: Scale,
          labelKey: 'nav.aneelPenalties',
          label: 'Penalty bands',
        },
        {
          to: '/aneel/consistency',
          icon: GitCompare,
          labelKey: 'nav.aneelConsistency',
          label: 'Divergent cases',
        },
        {
          to: '/aneel/collection',
          icon: Banknote,
          labelKey: 'nav.aneelCollection',
          label: 'Fine collection',
        },
        {
          to: '/aneel/fees',
          icon: Receipt,
          labelKey: 'nav.aneelFees',
          label: 'Inspection fee',
        },
        {
          to: '/aneel/notifications',
          icon: Search,
          labelKey: 'nav.aneelNotifications',
          label: 'Inspection funnel',
        },
        {
          to: '/aneel/timeline',
          icon: TrendingUp,
          labelKey: 'nav.aneelTimeline',
          label: 'How long cases take',
        },
        {
          to: '/aneel/bidding',
          icon: Gavel,
          labelKey: 'nav.aneelBidding',
          label: 'Intimations & edital penalties',
        },
        {
          to: '/aneel/appeals',
          icon: Landmark,
          labelKey: 'nav.aneelAppeals',
          label: 'Appeals',
        },
        {
          to: '/aneel/entities',
          icon: Building2,
          labelKey: 'nav.aneelEntities',
          label: 'Companies',
        },
      ],
    });
  }
  // The CADE group, added when its first screen was built -- which is the
  // condition the note previously here set out. A group whose children link at
  // nothing is worse than no group, because the catch-all redirects rather than
  // 404s and the dead link lands on a valid-looking page.
  if (mayAccess('cade')) {
    entries.push({
      key: 'cade',
      icon: Gavel,
      labelKey: 'nav.groupCade',
      label: 'CADE',
      children: [
        {
          to: '/cade/start',
          icon: Compass,
          labelKey: 'nav.cadeStart',
          label: 'First steps',
        },
        {
          to: '/cade/overview',
          icon: BarChart3,
          labelKey: 'nav.cadeOverview',
          label: 'CADE tribunal',
        },
        {
          to: '/cade/cases',
          icon: ListFilter,
          labelKey: 'nav.cadeCases',
          label: 'CADE cases',
        },
        {
          to: '/cade/decisions',
          icon: Search,
          labelKey: 'nav.cadeDecisions',
          label: 'CADE decisions',
        },
        {
          to: '/cade/documents',
          icon: FileSearch,
          labelKey: 'nav.cadeDocuments',
          label: 'CADE documents',
        },
        {
          to: '/cade/company',
          icon: Building2,
          labelKey: 'nav.cadeCompany',
          label: 'CADE company check',
        },
        {
          to: '/cade/sessions',
          icon: CalendarDays,
          labelKey: 'nav.cadeSessions',
          label: 'CADE sessions',
        },
        {
          to: '/cade/activity',
          icon: TrendingUp,
          labelKey: 'nav.cadeActivity',
          label: 'CADE by year',
        },
        {
          to: '/cade/consistency',
          icon: GitCompare,
          labelKey: 'nav.cadeConsistency',
          label: 'CADE consistency',
        },
        {
          to: '/cade/commissioners',
          icon: Users,
          labelKey: 'nav.cadeCommissioners',
          label: 'CADE rapporteurs',
        },
        {
          to: '/cade/sanctions',
          icon: Scale,
          labelKey: 'nav.cadeSanctions',
          label: 'CADE fines',
        },
        {
          to: '/cade/conselho',
          icon: Users,
          labelKey: 'nav.cadeConselho',
          label: 'CADE Tribunal track',
        },
        {
          to: '/cade/parties',
          icon: Users,
          labelKey: 'nav.cadeParties',
          label: 'CADE parties',
        },
        {
          to: '/cade/positioning',
          icon: Scale,
          labelKey: 'nav.cadePositioning',
          label: 'Position a case',
        },
        {
          to: '/cade/vistas',
          icon: Hourglass,
          labelKey: 'nav.cadeVista',
          label: 'Pedidos de vista',
        },
        {
          to: '/cade/bulletin',
          icon: Newspaper,
          labelKey: 'nav.cadeBulletin',
          label: 'Bulletin draft',
        },
        {
          to: '/cade/compare',
          icon: ArrowLeftRight,
          labelKey: 'nav.cadeCompare',
          label: 'Compare cases',
        },
        {
          to: '/cade/audio',
          icon: Mic,
          labelKey: 'nav.cadeAudio',
          label: 'Session audio',
        },
        {
          to: '/cade/method',
          icon: BookOpen,
          labelKey: 'nav.cadeMethod',
          label: 'Method and limits',
        },
      ],
    });
  }
  if (mayAccess('ans')) {
    entries.push({
      key: 'ans',
      icon: HeartPulse,
      labelKey: 'nav.groupAns',
      label: 'ANS',
      children: [
        {
          to: '/ans/overview',
          icon: BarChart3,
          labelKey: 'nav.ansOverview',
          label: 'ANS penalties',
        },
        {
          to: '/ans/cases',
          icon: ListFilter,
          labelKey: 'nav.ansCases',
          label: 'Penalties',
        },
        { to: '/ans/appeals', icon: Scale, labelKey: 'nav.ansAppeals', label: 'Board review' },
        { to: '/ans/penalties', icon: Coins, labelKey: 'nav.ansPenalties', label: 'Fines' },
        {
          to: '/ans/collection',
          icon: Receipt,
          labelKey: 'nav.ansCollection',
          label: 'Collection',
        },
        {
          to: '/ans/consistency',
          icon: GitCompare,
          labelKey: 'nav.ansConsistency',
          label: 'Consistency',
        },
        { to: '/ans/timeline', icon: Clock, labelKey: 'nav.ansTimeline', label: 'Timeline' },
        { to: '/ans/nip', icon: MessageSquare, labelKey: 'nav.ansNip', label: 'Consumer demands' },
        {
          to: '/ans/sus',
          icon: HeartHandshake,
          labelKey: 'nav.ansSus',
          label: 'SUS reimbursement',
        },
        {
          to: '/ans/regimes',
          icon: ShieldAlert,
          labelKey: 'nav.ansRegimes',
          label: 'Special regimes',
        },
        {
          to: '/ans/entities',
          icon: Building2,
          labelKey: 'nav.ansEntities',
          label: 'Operators',
        },
      ],
    });
  }
  if (mayAccess('ibama')) {
    entries.push({
      key: 'ibama',
      icon: TreePine,
      labelKey: 'nav.groupIbama',
      label: 'IBAMA',
      children: [
        {
          to: '/ibama/overview',
          icon: BarChart3,
          labelKey: 'nav.ibamaOverview',
          label: 'IBAMA enforcement',
        },
        {
          to: '/ibama/cases',
          icon: ListFilter,
          labelKey: 'nav.ibamaCases',
          label: 'Ledger cases',
        },
        {
          to: '/ibama/decisions',
          icon: Scale,
          labelKey: 'nav.ibamaDecisions',
          label: 'Instances',
        },
        {
          to: '/ibama/penalties',
          icon: Coins,
          labelKey: 'nav.ibamaPenalties',
          label: 'Face values',
        },
        {
          to: '/ibama/collection',
          icon: Receipt,
          labelKey: 'nav.ibamaCollection',
          label: 'Collection',
        },
        {
          to: '/ibama/consistency',
          icon: GitCompare,
          labelKey: 'nav.ibamaConsistency',
          label: 'Comparable groups',
        },
        {
          to: '/ibama/timeline',
          icon: Clock,
          labelKey: 'nav.ibamaTimeline',
          label: 'Timeline',
        },
        {
          to: '/ibama/notifications',
          icon: MessageSquare,
          labelKey: 'nav.ibamaNotifications',
          label: 'Notifications',
        },
        {
          to: '/ibama/fees',
          icon: Banknote,
          labelKey: 'nav.ibamaFees',
          label: 'TCFA levy',
        },
        {
          to: '/ibama/instruments',
          icon: ShieldAlert,
          labelKey: 'nav.ibamaInstruments',
          label: 'Instruments',
        },
        {
          to: '/ibama/judges',
          icon: Users,
          labelKey: 'nav.ibamaJudges',
          label: 'Judges',
        },
        {
          to: '/ibama/conama/board',
          icon: Landmark,
          labelKey: 'nav.ibamaConama',
          label: 'CONAMA CER',
        },
        {
          to: '/ibama/judges/methodology',
          icon: FileText,
          labelKey: 'nav.ibamaJudgesMethodology',
          label: 'Judges methodology',
        },
        {
          to: '/ibama/entities',
          icon: Building2,
          labelKey: 'nav.ibamaEntities',
          label: 'Companies',
        },
      ],
    });
  }
  if (mayAccess('bacen')) {
    entries.push({
      key: 'bacen',
      icon: Landmark,
      labelKey: 'nav.groupBacen',
      label: 'BACEN',
      children: [
        {
          to: '/bacen/overview',
          icon: BarChart3,
          labelKey: 'nav.bacenOverview',
          label: 'BACEN sanctions',
        },
        {
          to: '/bacen/cases',
          icon: ListFilter,
          labelKey: 'nav.bacenCases',
          label: 'Ledger rows',
        },
        {
          to: '/bacen/appeals',
          icon: Scale,
          labelKey: 'nav.bacenAppeals',
          label: 'CRSFN review',
        },
        {
          to: '/bacen/penalties',
          icon: Coins,
          labelKey: 'nav.bacenPenalties',
          label: 'Fines',
        },
        {
          to: '/bacen/collection',
          icon: Receipt,
          labelKey: 'nav.bacenCollection',
          label: 'Collection',
        },
        {
          to: '/bacen/consistency',
          icon: GitCompare,
          labelKey: 'nav.bacenConsistency',
          label: 'Consistency',
        },
        {
          to: '/bacen/timeline',
          icon: Clock,
          labelKey: 'nav.bacenTimeline',
          label: 'Timeline',
        },
        {
          to: '/bacen/registers',
          icon: ShieldAlert,
          labelKey: 'nav.bacenRegisters',
          label: 'Registers',
        },
        {
          to: '/bacen/settlements',
          icon: HeartHandshake,
          labelKey: 'nav.bacenSettlements',
          label: 'Settlements',
        },
        {
          to: '/bacen/entities',
          icon: Building2,
          labelKey: 'nav.bacenEntities',
          label: 'Companies',
        },
        {
          to: '/bacen/crsfn/board',
          icon: Gavel,
          labelKey: 'nav.bacenCrsfnBoard',
          label: 'CRSFN board',
        },
        {
          to: '/bacen/crsfn/relators',
          icon: Users,
          labelKey: 'nav.bacenCrsfnRelators',
          label: 'CRSFN relators',
        },
        {
          to: '/bacen/crsfn/votes',
          icon: Vote,
          labelKey: 'nav.bacenCrsfnVotes',
          label: 'CRSFN votes',
        },
        {
          to: '/bacen/crsfn/dispositions',
          icon: FileText,
          labelKey: 'nav.bacenCrsfnDispositions',
          label: 'CRSFN rulings',
        },
        {
          to: '/bacen/crsfn/search',
          icon: Search,
          labelKey: 'nav.bacenCrsfnSearch',
          label: 'CRSFN search',
        },
      ],
    });
  }
  if (mayAccess('cvm')) {
    entries.push({
      key: 'cvm',
      icon: ChartCandlestick,
      labelKey: 'nav.groupCvm',
      label: 'CVM',
      children: [
        { to: '/cvm/overview', icon: BarChart3, labelKey: 'nav.cvmOverview', label: 'Overview' },
        { to: '/cvm/cases', icon: ListFilter, labelKey: 'nav.cvmCases', label: 'Judged cases' },
        {
          to: '/cvm/decisions',
          icon: FileSearch,
          labelKey: 'nav.cvmDecisions',
          label: 'Text search',
        },
        { to: '/cvm/sanctions', icon: Coins, labelKey: 'nav.cvmSanctions', label: 'Sanctions' },
        { to: '/cvm/votes', icon: Vote, labelKey: 'nav.cvmVotes', label: 'Votes' },
        { to: '/cvm/directors', icon: Users, labelKey: 'nav.cvmDirectors', label: 'Directors' },
        {
          to: '/cvm/sessions',
          icon: CalendarDays,
          labelKey: 'nav.cvmSessions',
          label: 'Sessions',
        },
        { to: '/cvm/parties', icon: UserRound, labelKey: 'nav.cvmParties', label: 'Accused' },
        {
          to: '/cvm/settlements',
          icon: Handshake,
          labelKey: 'nav.cvmSettlements',
          label: 'Settlements',
        },
        { to: '/cvm/appeals', icon: Gavel, labelKey: 'nav.cvmAppeals', label: 'CRSFN appeals' },
        {
          to: '/cvm/pipeline',
          icon: ListOrdered,
          labelKey: 'nav.cvmPipeline',
          label: 'Process register',
        },
        {
          to: '/cvm/consistency',
          icon: GitCompare,
          labelKey: 'nav.cvmConsistency',
          label: 'Comparables',
        },
      ],
    });
  }
  // Administration is gated on the SUPERADMIN role, not on an agency
  // entitlement, so it does not go through mayAccess. Anyone else never sees
  // the group, would be redirected by the route guard if they typed the URL,
  // and would get a 403 from the API on top of that.
  // The outcome review sits beside it for admins and superadmins who reach CADE: a review changes
  // what every reader sees once the ETL applies it, so `visitante` never gets it.
  const reviews = isReviewer && mayAccess('cade');
  if (isSuperadmin || reviews) {
    entries.push({
      key: 'admin',
      icon: Users,
      labelKey: 'nav.groupAdmin',
      label: 'Administration',
      children: [
        ...(isSuperadmin
          ? [
              {
                to: '/admin/users',
                icon: Users,
                labelKey: 'nav.adminUsers',
                label: 'Users and access',
              },
            ]
          : []),
        ...(reviews
          ? [
              {
                to: '/admin/outcomes',
                icon: ClipboardCheck,
                labelKey: 'nav.adminOutcomes',
                label: 'CADE outcome review',
              },
            ]
          : []),
      ],
    });
  }
  // The process detail route is deliberately NOT here. Detail pages take an id
  // and are reached by clicking a row.
  // Every nav entry names ONE screen and nothing beneath it. `NavLink` defaults
  // to prefix matching, so without this `/anatel/paf` also lights up while the
  // user is on `/anatel/paf/consistency` -- two entries drawn as "you are here"
  // at once, which is worse than none, and the only pair that collides today.
  // Applied to all of them rather than to that pair, so the next nested route
  // cannot reintroduce it.
  return entries.map((entry) =>
    'children' in entry
      ? { ...entry, children: entry.children.map((child) => ({ ...child, end: true })) }
      : entry,
  );
}

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function AppLayout() {
  const { user, logout, mayAccess } = useAuth();
  const { t } = useTranslation();

  const displayName = user?.full_name ?? user?.email ?? '';
  const sidebarUser = user
    ? {
        name: displayName,
        role: user.role.charAt(0).toUpperCase() + user.role.slice(1),
        initials: initialsOf(displayName),
      }
    : undefined;

  return (
    <div className="h-screen w-screen">
      <SidebarLayout
        navigation={buildNavigation(
          mayAccess,
          user?.role === 'superadmin',
          !!user && user.role !== 'viewer',
          user?.role === 'admin' || user?.role === 'superadmin',
        )}
        brandNameKey="branding.name"
        brandSubtitleKey="branding.subtitle"
        user={sidebarUser}
        onLogout={logout}
        headerActions={
          <>
            {/* Launcher for the sibling platform. It is a SEPARATE deployable
                with its own session, so this is a link to its own origin,
                opened in a new tab -- never an iframe, which would partition
                the localStorage its token lives in. Hidden entirely when the
                URL is blank, rather than shipping a button pointing nowhere.

                The label collapses to the icon below lg: this header already
                carries three switchers, and unlike the sibling's public header
                there is no footer here to fall back to, so the destination has
                to stay reachable at every width. `aria-label` is constant and
                the visible text is a substring of it, per WCAG 2.5.3. */}
            {branding.precatoriosPlatformUrl && (
              <a
                href={branding.precatoriosPlatformUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('launcher.precatorios')}
                title={t('launcher.precatorios')}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-all duration-200 hover:bg-primary/90 hover:scale-[1.02]"
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                <span className="hidden lg:inline">{t('launcher.precatorios')}</span>
              </a>
            )}
            <ThemeSwitcher />
            <LanguageSwitcher />
            <TimezoneSelector />
          </>
        }
      >
        <Outlet />
      </SidebarLayout>
    </div>
  );
}
